import * as React from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import {
  AlertTriangle,
  ArrowRight,
  BookOpen,
  Compass,
  GraduationCap,
  Heart,
  Loader2,
  Settings2,
  Sparkles,
} from 'lucide-react'
import { CourseCard } from './-components/CourseCard'
import { CourseRow } from './-components/CourseRow'
import { RequestCourseDialog } from './-components/RequestCourseDialog'
import type { MyLearningCourse } from '@/types/learning.types'
import { PageTitle } from '@/components/PageTitle'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { ProgressRing } from '@/components/shared/ProgressRing'
import { useAuth } from '@/contexts/AuthContext'
import { useCan } from '@/hooks/permissions/use-can'
import { useMyLearning } from '@/hooks/repositories/useLearningRepository'
import { cn } from '@/lib/utils'
import { LEARNING_PERMISSION_KEYS } from '@/lib/permissions/learning-permissions'

export const Route = createFileRoute('/_app/learning/')({
  component: RouteComponent,
})

function Section({
  title,
  count,
  icon,
  layout = 'grid',
  children,
}: {
  title: string
  count: number
  icon?: React.ReactNode
  /** Rows for work you owe, cards for things you might pick up. */
  layout?: 'grid' | 'rows'
  children: React.ReactNode
}) {
  if (count === 0) return null
  return (
    <section className="mb-8">
      <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold">
        {icon}
        {title}
        <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground tabular-nums">
          {count}
        </span>
      </h2>
      <div
        className={
          layout === 'rows'
            ? 'flex flex-col gap-2'
            : 'grid gap-4 sm:grid-cols-2 lg:grid-cols-3'
        }
      >
        {children}
      </div>
    </section>
  )
}

function cards(courses: Array<MyLearningCourse>) {
  return courses.map((course) => (
    <CourseCard key={course.assignmentId ?? course.courseId} course={course} />
  ))
}

function rows(courses: Array<MyLearningCourse>) {
  return courses.map((course) => (
    <CourseRow key={course.assignmentId ?? course.courseId} course={course} />
  ))
}

/**
 * The learner's home for Learning (sc-1245).
 *
 * Written for the person taking training, not the person writing it — that is
 * what `/learning/admin` is for, and the link to it is a quiet secondary action
 * rather than the centre of the page. Almost everyone who lands here has no
 * authoring permission at all.
 *
 * The order is deliberate and is the whole design: resume what you started,
 * then start what you owe, then browse, then look back at what you finished.
 * Overdue work is lifted out of that order entirely, because it is the only
 * thing on the page that needs attention today.
 */
function RouteComponent() {
  const { user } = useAuth()
  const { has } = useCan()
  const { data, isPending } = useMyLearning()

  // "Admin" here means holding any of the three admin-side Learning keys —
  // writing courses, assigning them, or reporting on them. Not the department:
  // the permission system is how this app decides access, and an ADMIN already
  // holds all three through the catalog short-circuit.
  const isLearningAdmin =
    has(LEARNING_PERMISSION_KEYS.author) ||
    has(LEARNING_PERMISSION_KEYS.assign) ||
    has(LEARNING_PERMISSION_KEYS.reports)

  const firstName = user?.firstName ?? 'there'

  // Ordered by urgency, not by status: overdue, then resumed, then untouched.
  const owed = React.useMemo(() => {
    const overdue = [
      ...(data?.inProgress ?? []),
      ...(data?.assigned ?? []),
    ].filter((course) => course.isOverdue)
    const inProgress = (data?.inProgress ?? []).filter((c) => !c.isOverdue)
    const assigned = (data?.assigned ?? []).filter((c) => !c.isOverdue)
    return [...overdue, ...inProgress, ...assigned]
  }, [data])
  const stats = data?.stats
  const hasAnything =
    (data?.assigned.length ?? 0) +
      (data?.inProgress.length ?? 0) +
      (data?.completed.length ?? 0) +
      (data?.favorites.length ?? 0) +
      (data?.catalog.length ?? 0) >
    0

  return (
    <div className="py-4">
      <PageTitle title="Learning" />

      {/* ── Greeting ───────────────────────────────────────────────────── */}
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-xl font-bold tracking-tight sm:text-2xl">
            Hey {firstName},
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {stats?.coursesOverdue
              ? `You have ${stats.coursesOverdue} overdue ${stats.coursesOverdue === 1 ? 'course' : 'courses'}.`
              : stats?.lessonsTotal
                ? 'Here’s where you are with your training.'
                : 'Training and policy acknowledgment, in one tracked place.'}
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {/* Browse used to appear only when the catalogue preview had
              something to show, so anyone already assigned every published
              course lost the entrance to it entirely. It is a permanent part
              of the page now. */}
          <Button asChild variant="outline" size="sm">
            <Link to="/learning/browse">
              <Compass className="mr-1 size-4" aria-hidden />
              Browse courses
            </Link>
          </Button>
          <RequestCourseDialog />
          {/* Admin tools are a side door, not the point of this page. Gated on
              the ADMIN-side keys rather than on `learning.` — every learner
              holds learning.catalog, so the prefix would show this to
              everybody. */}
          {isLearningAdmin && (
            <Button asChild variant="ghost" size="sm">
              <Link to="/learning/admin">
                <Settings2 className="mr-1 size-4" aria-hidden />
                Admin tools
              </Link>
            </Button>
          )}
        </div>
      </div>

      {isPending ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          {/* ── Progress ─────────────────────────────────────────────── */}
          {/* Courses lead. The unit a person is assigned, chased about and
              judged on is the COURSE — lessons are how far through one you are.
              The lesson roll-up used to be the biggest number on the page,
              which answered a question nobody asks. */}
          {stats && stats.lessonsTotal > 0 && (
            <div className="mb-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Card
                className={owed.length > 0 ? 'border-primary/30' : undefined}
              >
                <CardContent className="py-5">
                  <div className="mb-2 inline-flex rounded-lg bg-primary/10 p-2 text-primary">
                    <BookOpen className="size-4" aria-hidden />
                  </div>
                  <p className="text-2xl font-bold tabular-nums sm:text-3xl">
                    {owed.length}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {owed.length === 1 ? 'course' : 'courses'} to complete
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="py-5">
                  <div className="mb-2 inline-flex rounded-lg bg-emerald-500/10 p-2 text-emerald-700 dark:text-emerald-300">
                    <Sparkles className="size-4" aria-hidden />
                  </div>
                  <p className="text-2xl font-bold tabular-nums sm:text-3xl">
                    {stats.coursesCompleted}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {stats.coursesCompleted === 1 ? 'course' : 'courses'}{' '}
                    completed
                  </p>
                </CardContent>
              </Card>

              <Card
                className={
                  stats.coursesOverdue > 0 ? 'border-destructive/40' : undefined
                }
              >
                <CardContent className="py-5">
                  <div
                    className={
                      stats.coursesOverdue > 0
                        ? 'mb-2 inline-flex rounded-lg bg-destructive/10 p-2 text-destructive'
                        : 'mb-2 inline-flex rounded-lg bg-muted p-2 text-muted-foreground'
                    }
                  >
                    <AlertTriangle className="size-4" aria-hidden />
                  </div>
                  <p
                    className={cn(
                      'text-2xl font-bold tabular-nums sm:text-3xl',
                      stats.coursesOverdue > 0 && 'text-destructive',
                    )}
                  >
                    {stats.coursesOverdue}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {stats.coursesOverdue === 1 ? 'course' : 'courses'} overdue
                  </p>
                </CardContent>
              </Card>

              {/* The roll-up, kept but demoted: useful for a sense of momentum
                  across everything, not a number to act on. */}
              <Card>
                <CardContent className="flex items-center gap-3 py-5">
                  <ProgressRing
                    percent={stats.percentComplete}
                    size={52}
                    strokeWidth={5}
                  >
                    <span className="text-[11px] font-semibold tabular-nums">
                      {stats.percentComplete}
                    </span>
                  </ProgressRing>
                  <div className="min-w-0">
                    <p className="text-base font-semibold tabular-nums">
                      {stats.lessonsCompleted}
                      <span className="font-normal text-muted-foreground">
                        {' '}
                        / {stats.lessonsTotal}
                      </span>
                    </p>
                    <p className="text-xs text-muted-foreground">
                      lessons across all courses
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* One grid rather than three. Overdue, in-progress and assigned are
              all "things you owe", and a section per state left a single card
              stranded on its own row. They keep their ORDER — overdue first,
              then what you started, then what you have not — and each card
              carries its own state, so nothing is lost by merging them. */}
          <Section
            title="Courses assigned to you"
            count={owed.length}
            layout="rows"
          >
            {rows(owed)}
          </Section>

          {/* Under your training, above Browse: things you chose to keep rank
              over the catalogue, but never over what you actually owe. */}
          {(data?.favorites.length ?? 0) > 0 && (
            <Section
              title="Favourites"
              count={data?.favorites.length ?? 0}
              icon={
                <Heart
                  className="size-4 fill-rose-500 text-rose-500"
                  aria-hidden
                />
              }
            >
              {cards(data?.favorites ?? [])}
            </Section>
          )}

          {(data?.catalog.length ?? 0) > 0 && (
            <section className="mb-8">
              <div className="mb-3 flex items-center justify-between">
                <h2 className="flex items-center gap-2 text-sm font-semibold">
                  More in the catalogue
                </h2>
                {/* Always offered, not just when the preview overflows: the
                    landing shows a handful, and "where is everything else" is
                    a question a taste always raises. */}
                <Button asChild variant="ghost" size="sm">
                  <Link to="/learning/browse">
                    {data?.catalogHasMore ? 'See all courses' : 'Browse all'}
                    <ArrowRight className="ml-1 size-4" aria-hidden />
                  </Link>
                </Button>
              </div>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {cards(data?.catalog ?? [])}
              </div>
            </section>
          )}

          <Section
            title="Completed"
            count={data?.completed.length ?? 0}
            layout="rows"
          >
            {rows(data?.completed ?? [])}
          </Section>

          {!hasAnything && (
            <Card>
              <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
                <div className="rounded-full bg-muted p-3">
                  <GraduationCap
                    className="size-6 text-muted-foreground"
                    aria-hidden
                  />
                </div>
                <div>
                  <p className="text-sm font-medium">Nothing here yet</p>
                  <p className="mt-1 max-w-sm text-sm text-muted-foreground">
                    When a course is published and assigned to you, it will show
                    up here with its due date.
                  </p>
                </div>
                {has(LEARNING_PERMISSION_KEYS.author) && (
                  <Button asChild variant="outline" size="sm" className="mt-2">
                    <Link to="/learning/admin">
                      <Compass className="mr-1 size-4" aria-hidden />
                      Create the first course
                    </Link>
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  )
}
